import React, { Fragment, useState, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import ForgotPasswordForm from './ForgotPasswordForm';
import Button from '../Button';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialView?: 'login' | 'signup' | 'forgot-password';
}

const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  initialView = 'login'
}) => {
  const [view, setView] = useState<'login' | 'signup' | 'forgot-password'>(initialView);

  const handleClose = useCallback(() => {
    onClose();
    // Reset view state when modal closes
    setTimeout(() => setView(initialView), 200);
  }, [onClose, initialView]);

  const switchView = (newView: 'login' | 'signup' | 'forgot-password') => {
    setView(newView);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title className="text-lg font-medium leading-6 text-gray-900">
                    {view === 'login' && 'Log in'}
                    {view === 'signup' && 'Sign up'}
                    {view === 'forgot-password' && 'Reset password'}
                  </Dialog.Title>
                  <Button
                    variant="secondary"
                    onClick={handleClose}
                    className="p-1 hover:bg-gray-100 rounded-full"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                  </Button>
                </div>

                {view !== 'forgot-password' && (
                  <div className="flex space-x-4 mb-6">
                    <button
                      onClick={() => switchView('login')}
                      className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                        view === 'login'
                          ? 'border-airbnb-red text-airbnb-red'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Log in
                    </button>
                    <button
                      onClick={() => switchView('signup')}
                      className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                        view === 'signup'
                          ? 'border-airbnb-red text-airbnb-red'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Sign up
                    </button>
                  </div>
                )}

                <div className="mt-4">
                  {view === 'login' && (
                    <LoginForm
                      onForgotPassword={() => switchView('forgot-password')}
                      onClose={handleClose}
                    />
                  )}
                  {view === 'signup' && <SignupForm onClose={handleClose} />}
                  {view === 'forgot-password' && (
                    <ForgotPasswordForm
                      onBackToLogin={() => switchView('login')}
                      onClose={handleClose}
                    />
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <Button
                    variant="primary"
                    onClick={handleClose}
                    className="w-full sm:w-auto"
                  >
                    J'ai compris
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AuthModal;